import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  RouteProps,
  Redirect,
  Switch,
  //useLocation
} from 'react-router-dom';

import {
  groupAuthInformation,
  GroupToken,
  storageAuthInformation,
  storageAuthToken,
  storageRoles,
} from '../common/constants/strings';
import MainLayout from '../common/layouts/main';

// Import application features
import NotFound from '../common/components/pageNotFound';
import Login from '../features/account/login/login';
import ForgotPassword from '../features/account/forgotPassword/forgotPassword';
import ResetPassword from '../features/account/resetPassword/resetPassword';

import Users from '../features/users/components/users';
import Exhibits from '../features/exhibits/components/exhibits';
import ExhibitForm from '../features/exhibits/components/form/exhibitForm';
import VisitorInfo from '../features/visitorInformation/components/visitorInfo';
import GroupVisits from '../features/groupVisits/components/groupVisits';
import GroupInfo from '../features/groupVisits/components/form/groupInfo';
import UserDetails from '../features/users/components/form/userDetails';
import NotificationCard from '../features/notifications/components/card/notificationCard';
import GroupVisitLogin from '../features/groupVisits/components/summary/login/components/groupVisitLogin';
import GroupVisitSummary from '../features/groupVisits/components/summary/groupVisitSummary';
import AppDownload from '../features/appDownload/appDownload';
import GroupVisitPasswordResetForm from '../features/groupVisits/components/summary/login/components/groupVisitPasswordResetForm';

// NOTE: You cannot use any react-router-dom hooks in the component that creates the router.
const AppRouter: React.FC = () => {
  return (
    <Router>
      <Switch>
        <Route exact path='/login' component={Login} />
        <Route exact path='/forgot-password' component={ForgotPassword} />
        <Route exact path='/reset-password' component={ResetPassword} />
        <Route
          exact
          path='/reset-password/:id'
          component={GroupVisitPasswordResetForm}
        />
        <Route exact path='/visit-login/:id' component={GroupVisitLogin} />
        <Route exact path='/visit-summary/:id' component={GroupVisitSummary} />
        <Route exact path='/qrCodes/:id' component={AppDownload} />
        <RoutePrivate path='/' component={LoggedInRouter} />
        <RoutePrivateGV path='/' component={GroupVisitRouter} />
      </Switch>
    </Router>
  );
};

export default AppRouter;

const LoggedInRouter: React.FC = () => (
  <Switch>
    <Route exact path='/' render={() => <Redirect to='/login' />} />

    <RouteWithLayout
      exact
      path='/users'
      component={Users}
      layout={MainLayout}
    />
    <RouteWithLayout
      exact
      path={['/users/create', '/users/:id']}
      component={UserDetails}
      layout={MainLayout}
    />

    <RouteWithLayout
      exact
      path='/exhibits'
      component={Exhibits}
      layout={MainLayout}
    />
    <RouteWithLayout
      exact
      path={['/exhibits/create', '/exhibits/:id']}
      component={ExhibitForm}
      layout={MainLayout}
    />

    <RouteWithLayout
      exact
      path='/visitor-information'
      component={VisitorInfo}
      layout={MainLayout}
    />

    <RouteWithLayout
      exact
      path='/group-visits'
      component={GroupVisits}
      layout={MainLayout}
    />
    <RouteWithLayout
      exact
      path={['/group-visits/create', '/group-visits/:id']}
      component={GroupInfo}
      layout={MainLayout}
    />

    <RouteWithLayout
      exact
      path='/notifications'
      component={NotificationCard}
      layout={MainLayout}
    />

    <RouteWithLayout component={NotFound} />
  </Switch>
);

const GroupVisitRouter: React.FC = () => (
  <Switch>
    <Route exact path='/' render={() => <Redirect to='/visit-login/:id' />} />

    <RouteWithLayout
      exact
      path='/visit-summary/:id'
      component={GroupVisitSummary}
    />

    <RouteWithLayout component={NotFound} />
  </Switch>
);

const RouteWithLayout = ({
  component: Component,
  layout: Layout,
  ...rest
}: any) => (
  <Route
    {...rest}
    render={(props) => {
      if (Layout) {
        return (
          <Layout>
            <Component {...props} />
          </Layout>
        );
      } else {
        return <Component {...props} />;
      }
    }}
  />
);

const RoutePrivate: React.FC<RouteProps> = ({
  component: Component,
  ...rest
}: RouteProps) => {
  const isAuthenticated = sessionStorage.getItem(storageAuthToken) ?? false;

  if (!Component) {
    return <Route {...rest} />;
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: props.location.pathname },
            }}
          />
        )
      }
    />
  );
};

const RoutePrivateGV: React.FC<RouteProps> = ({
  // add last index

  component: Component,
  ...rest
}: RouteProps) => {
  const isGroupAuthenticated = sessionStorage.getItem(GroupToken) ?? false;

  const groupInfo = sessionStorage.getItem(groupAuthInformation);

  if (!Component) {
    return <Route {...rest} />;
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        isGroupAuthenticated ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: `/visit-login/${groupInfo}`,
              state: { from: props.location.pathname },
            }}
          />
        )
      }
    />
  );
};
